import React, { useState } from "react";
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  Card,
  Box,
} from "@material-ui/core";
import "./style.css";
import {
  GetCompetitionsAsyncParams,
  PreviewFeaturedCompetition,
  PreviewTabFeatureCompetitionsState,
} from "./previewTabFeatureCompetitions";
import PaginationTable from "../../common/paginationTable";
import HeadTable from "../../common/sortTable/sortTable";
import CreditFilter from "../../users/CreditFilter";
import TablesSearch from "../../common/tablesSearch/tablesSearch";

interface PreviewFeaturedCompetitionsTableProps {
  getGeneralFeturedCompetitionsAsync: (
    params: GetCompetitionsAsyncParams
  ) => void;
  handleChangeIsTrending: (competition: PreviewFeaturedCompetition) => void;
  state: PreviewTabFeatureCompetitionsState;
  searchState: { title: string };
  filterState: { searchCompetitionType: Array<string> };
  setFilter: React.Dispatch<
    React.SetStateAction<{
      searchCompetitionType: string[];
      searchField: string;
    }>
  >;
  setSearch: React.Dispatch<
    React.SetStateAction<{
      title: string;
    }>
  >;
}

export const PreviewFeaturedCompetitionsTable: React.FC<PreviewFeaturedCompetitionsTableProps> =
  ({
    state,
    getGeneralFeturedCompetitionsAsync,
    handleChangeIsTrending,
    searchState,
    filterState,
    setFilter,
    setSearch,
  }) => {
    const [dropdown, setDropdown] = useState({
      competition: false,
      title: false
    });

    const handleChangeRowsPerPage = (
      event: React.ChangeEvent<HTMLInputElement>
    ): void => {
      const perPage = parseInt(event.target.value, 10);
      getGeneralFeturedCompetitionsAsync({
        page: 1,
        perPage: perPage,
        searchTitle: searchState.title,
        searchCompetitionType: filterState.searchCompetitionType,
      });
    };

    const handleLastPage = () => {
      const page = Math.ceil(state.allCount / state.perPage);
      getGeneralFeturedCompetitionsAsync({
        page: page,
        perPage: state.perPage,
        searchTitle: searchState.title,
        searchCompetitionType: filterState.searchCompetitionType,
      });
    };

    const handleFirstPage = () => {
      getGeneralFeturedCompetitionsAsync({
        page: 1,
        perPage: state.perPage,
        searchTitle: searchState.title,
        searchCompetitionType: filterState.searchCompetitionType,
      });
    };

    const handleChangePage = (
      event: React.MouseEvent<HTMLButtonElement> | null,
      newPage: number
    ): void => {
      getGeneralFeturedCompetitionsAsync({
        page: newPage + 1,
        perPage: state.perPage,
        searchTitle: searchState.title,
        searchCompetitionType: filterState.searchCompetitionType,
      });
    };

    const filterDropdown = (title: string) => {
      const titleArray = title.toLowerCase().split(" ");
      setDropdown({ ...dropdown, [titleArray[0]]: !dropdown[titleArray[0]] });
    };

    const closeFilter = () => {
      setDropdown({
        competition: false,
        title: false
      })
    }

    const search = (value: string, field: string) => {
      setSearch((state) => ({ ...state, [field]: value }));
    };
    const applySearch = (field: string) => {
      setFilter({ ...filterState, searchField: field });
      getGeneralFeturedCompetitionsAsync({
        page: 1,
        perPage: state.perPage,
        searchTitle: searchState.title,
        searchCompetitionType: filterState.searchCompetitionType,
      });
    };
    const clearSearch = (clear: string) => {
      setSearch((state) => ({ ...state, [clear]: "" }));
    };
    const applyFilter = (field: string, conditions: Array<string>) => {
      setFilter((state) => ({ ...state, searchCompetitionType: conditions }));
      getGeneralFeturedCompetitionsAsync({
        page: 1,
        perPage: state.perPage,
        searchTitle: searchState.title,
        searchCompetitionType: conditions,
      });
    };

    const getOrderNumber = (page: number, i: number) => {
      if (page === 1) {
        return i + 1
      } else {
        return (page - 1) * state.perPage + i + 1
      }
    }

    return (
      <Card>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <HeadTable
                  title="#"
                />
              </TableCell>
              <TableCell>
                <HeadTable
                  title="Title"
                  filterHandle={filterDropdown}
                />
                {dropdown.title && (
                  <TablesSearch
                    search={(event) =>
                      search(event.currentTarget.value, "title")
                    }
                    applySearch={() => applySearch("title")}
                    searchString={searchState.title}
                    clearSearch={() => clearSearch("title")}
                  />
                )}
              </TableCell>
              <TableCell id="active-table-head">
                <HeadTable
                  title="Competition type"
                  filterHandle={filterDropdown}
                />
                {dropdown.competition && (
                  <CreditFilter
                    firstCheckboxTitle="DREAMHOME"
                    secondCheckboxTitle="FIXED_ODDS"
                    thirdCheckboxTitle="PRIZE"
                    applyFilter={applyFilter}
                    field="competitionType"
                    selectedItem={filterState.searchCompetitionType}
                  />
                )}
              </TableCell>
              <TableCell>
                <HeadTable
                  title="Trending"
                />
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody onClick={closeFilter}>
            {state.competitions.length > 0 &&
              state.competitions.map((item, i) => (
                <TableRow>
                  <TableCell>{(getOrderNumber(state.page, i))}</TableCell>
                  <TableCell>{item.title}</TableCell>
                  <TableCell>{item.competitionType}</TableCell>
                  <TableCell>
                    <Box display="flex" justifyContent="center">
                      <label className="switch">
                        <input
                          type="checkbox"
                          checked={item.isTrending}
                          onClick={() => handleChangeIsTrending(item)}
                        />
                        <span className="slider round"></span>
                      </label>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
        <PaginationTable
          page={state.page}
          perPage={state.perPage}
          collectionLength={state.competitions.length}
          total={state.allCount}
          handleChangePage={handleChangePage}
          handleLastPage={handleLastPage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          handleFirstPage={handleFirstPage}
          title="Featured competitions"
        />
      </Card>
    );
  };
